import { useCallback, useEffect, useState } from "react";

import { supabase } from "~/integrations/supabase/client";

export interface PaymentRecord {
  id: number;
  salesRequestId: string;
  amount: number;
  paymentMethod: string;
  reference?: string;
  image_url?: string;
  txInternalId?: string;
  createdAt: string;
  createdBy?: string; // User ID who registered the payment
  payment_status?: string; // Status of the payment (pending, completed, failed)
  // Check-specific fields
  check_number?: string;
  bank?: string;
  reception_date?: string;
  collection_date?: string;
  check_status?: string; // Status of the check (pending, in_portfolio, collected, paid, protested)
  // Verification fields
  is_verified?: boolean | null; // true=verified, false=pending verification, null=checks (use check_status)
  verified_by?: string; // User ID who verified the payment
  verified_at?: string; // Timestamp when payment was verified
  fintoc_movement_id?: string; // Fintoc movement ID (historical archive)
  rail_movement_id?: string; // Rail movement ID for bank transfer verification
}

export const usePayments = (salesRequestId: string | null) => {
  const [payments, setPayments] = useState<PaymentRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const fetchPayments = useCallback(async () => {
    if (!salesRequestId) {
      setPayments([]);
      return;
    }

    setLoading(true);
    try {
      // Fetch payments related to the sales request - all payments except cancelled
      const { data, error } = await (supabase as any)
        .from("payments")
        .select("*")
        .eq("sales_request", salesRequestId)
        .neq("payment_status", "cancelled");

      if (error) {
        throw error;
      }

      // Transform database format to frontend format
      const formattedPayments: PaymentRecord[] = data.map((item) => ({
        id: item.id,
        salesRequestId: item.sales_request || "",
        amount: item.amount || 0,
        paymentMethod: item.payment_method || "No especificado",
        reference: item.reference,
        image_url: item.image_url,
        txInternalId: item.tx_internal_id,
        createdAt: item.created_at,
        createdBy: item.created_by,
        payment_status: item.payment_status,
        // Map check-specific fields
        check_number: item.check_number,
        bank: item.bank,
        reception_date: item.reception_date,
        collection_date: item.collection_date,
        check_status: item.check_status,
        // Map verification fields
        is_verified: item.is_verified,
        verified_by: item.verified_by,
        verified_at: item.verified_at,
        fintoc_movement_id: item.fintoc_movement_id,
        rail_movement_id: item.rail_movement_id,
      }));

      setPayments(formattedPayments);
    } catch (error) {
      console.error("Error fetching payments for sales request:", error);
      setPayments([]);
    } finally {
      setLoading(false);
    }
  }, [salesRequestId]);

  // Function to manually trigger a refresh
  const refresh = useCallback(() => {
    setRefreshKey((prev) => prev + 1);
  }, []);

  useEffect(() => {
    fetchPayments();
  }, [fetchPayments, refreshKey]);

  return { payments, loading, refresh };
};
